import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import 'react-datepicker/dist/react-datepicker.css';
import { useNavigate, useParams } from 'react-router-dom';

import LoadingSkeletonModalTable from '@/components/modal/prompt/LoadingSkeletonModalTable';
import HaggleMonthPickerComponent from '@/components/table/common/DatePicker';
import { IPaginationTable } from '@/components/table/common/interfaces/IPaginationTable';
import HaggleMonthlySalesReportTable from '@/components/table/haggle-event/HaggleMonthlySalesReportTable';
import {
	DEFAULT_PAGE_SIZE,
	PAGE_SIZE_OPTIONS,
} from '@/components/utils/constants';
import {
	formatPrice,
	formatToUrlDate,
	formattedMonth,
	isValidDateString,
} from '@/components/utils/utils';
import { ApiQueryKeys } from '@/interfaces/api/ApiQueryKeys';
import { IHaggleEventMonthlySalesReport } from '@/interfaces/api/haggle-event/IHaggleEventSalesReport';
import { haggleApiService } from '@/services/haggle.service';
import { notificationService } from '@/services/notification.service';

const HaggleEventMonthReportDashboard = () => {
	const { date: dateParam } = useParams<{ date?: string }>();
	const navigate = useNavigate();
	const [selectedPickerDate, setSelectedPickerDate] = useState<Date>(
		new Date(),
	);
	const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
	const [pageNumber, setPageNumber] = useState(1);

	useEffect(() => {
		if (dateParam && isValidDateString(dateParam)) {
			// If URL date is valid, set the picker date
			// Add 'T00:00:00' to parse correctly regardless of timezone
			const urlDate = new Date(dateParam + 'T00:00:00');
			if (
				urlDate.getFullYear() !== selectedPickerDate.getFullYear() ||
				urlDate.getMonth() !== selectedPickerDate.getMonth()
			) {
				setSelectedPickerDate(urlDate);
			}
		} else {
			// If URL date is missing or invalid, navigate to the current month's report URL
			const currentMonthDateString = formatToUrlDate(new Date());
			navigate(`/user/dashboard/reports/${currentMonthDateString}`, {
				replace: true,
			});
			setSelectedPickerDate(new Date());
		}
	}, [dateParam, navigate]);

	const reportDateString = formatToUrlDate(selectedPickerDate);

	const handleDateChange = (newDate: Date | null) => {
		if (newDate) {
			setSelectedPickerDate(newDate); // Update the picker state first
			const newDateString = formatToUrlDate(newDate); // Format to 'YYYY-MM-01'

			// Navigate to the new date URL, triggering useEffect and query refetch
			navigate(`/user/dashboard/reports/${newDateString}`, { replace: true });
		}
	};

	const {
		data: haggleEvents,
		isLoading: haggleEventIsLoading,
		isError: haggleEventIsError,
		error: haggleEventError,
	} = useQuery<IHaggleEventMonthlySalesReport>({
		queryKey: [ApiQueryKeys.getHaggleEventMonthlySalesReport, reportDateString],
		queryFn: () =>
			haggleApiService.getHaggleEventMonthlySalesReport(reportDateString, {
				params: {
					'page[size]': pageSize,
					'page[number]': pageNumber,
				},
			}),
		enabled: !!reportDateString && isValidDateString(reportDateString),
		refetchOnWindowFocus: false,
	});

	useEffect(() => {
		if (haggleEventIsError) {
			const message =
				haggleEventError instanceof Error
					? haggleEventError.message
					: 'Unknown error fetching haggle events';
			notificationService.error(message);
			// Navigate away on error
			navigate('/user/dashboard/haggle-events');
		}
	}, [haggleEventIsError, haggleEventError, navigate]);

	const handlePaginationOptions = (
		collection: IHaggleEventMonthlySalesReport,
	): IPaginationTable => {
		return {
			pageSize: collection.pageSize,
			pageNumber: collection.pageNumber,
			pageCount: collection.pageCount,
			itemCount: collection.itemCount,
			pageSizeOptions: PAGE_SIZE_OPTIONS,
			setPageSize: setPageSize,
			setPageNumber: setPageNumber,
		};
	};

	return (
		<div className="bg-grey-5 min-h-content overflow-y-auto">
			{haggleEventIsLoading && <LoadingSkeletonModalTable />}

			{haggleEvents && !haggleEventIsLoading && (
				<div className="flex flex-col h-full grow gap-4">
					<div className="bg-white rounded-md shadow p-6 mb-0">
						<div className="flex items-center justify-between">
							<h2 className="text-xl font-semibold">
								{formattedMonth(reportDateString)} Haggle Event Summary
							</h2>

							<div className="flex items-center gap-3">
								<HaggleMonthPickerComponent
									selectedDate={selectedPickerDate}
									onChange={handleDateChange}
									dateFormat="MMMM yyyy"
									showMonthYearPicker
									className="p-2 border border-gray-300 rounded-md cursor-pointer"
									ariaLabel="Select month and year for the report"
								/>
							</div>
						</div>

						<div className="mb-4 p-3 bg-blue-50 rounded-md mt-4">
							<h3 className="font-semibold">Sales Summary</h3>
							<p>
								Total sales from completed transactions:{' '}
								<span className="font-semibold">
									{formatPrice(haggleEvents.data.totalSalesMade)}
								</span>
							</p>
							<p>
								Total profit from completed transactions:{' '}
								<span className="font-semibold">
									{formatPrice(haggleEvents.data.totalProfit)}
								</span>
							</p>
							<p>
								Total sales completed:{' '}
								<span className="font-semibold">
									{haggleEvents.data.totalDeals}
								</span>
							</p>
						</div>
					</div>

					<HaggleMonthlySalesReportTable
						haggle={haggleEvents}
						paginationOption={handlePaginationOptions(haggleEvents)}
					/>
				</div>
			)}
		</div>
	);
};
export default HaggleEventMonthReportDashboard;
